import en from '../../catalogs/en.json' with { type: 'json' };
import es from '../../catalogs/es.json' with { type: 'json' };

/**
 * User-facing text of the shared CDN catalogs for one locale, with an explicit fallback to English.
 *
 * Keys and raw codes are identifiers and are never shown or translated: a job state `waiting_turn` or an
 * error code `CONFLICT_VERSION` stays as it is, and the catalog supplies the explanation around it.
 */
export class Catalog {
	static #tables = { en, es };

	/**
	 * The locale every lookup falls back to
	 */
	static get fallback() {
		return 'en';
	}

	/**
	 * @returns {string[]}
	 */
	static get locales() {
		return Object.keys(Catalog.#tables);
	}

	#locale;
	get locale() {
		return this.#locale;
	}

	/**
	 * @param {string} [locale] A BCP 47 tag; `es-AR` selects `es`, an unsupported language selects the fallback
	 */
	constructor(locale = Catalog.fallback) {
		const language = String(locale).toLowerCase().split('-')[0];
		this.#locale = language in Catalog.#tables ? language : Catalog.fallback;
	}

	/**
	 * @param {string} key
	 */
	has(key) {
		return key in Catalog.#tables[this.#locale] || key in Catalog.#tables[Catalog.fallback];
	}

	/**
	 * @param {string} key
	 * @returns {{text: string, locale: string, fallback: boolean} | undefined} `fallback` tells that the
	 * locale lacks the key and the English text was used
	 */
	entry(key) {
		const own = Catalog.#tables[this.#locale][key];
		if (own !== undefined) return { text: own, locale: this.#locale, fallback: false };
		const text = Catalog.#tables[Catalog.fallback][key];
		return text === undefined ? undefined : { text, locale: Catalog.fallback, fallback: true };
	}

	/**
	 * @param {string} key
	 * @param {Record<string, string | number>} [values] Replacements for `{name}` placeholders
	 * @returns {string}
	 * @throws {RangeError} When no catalog holds the key, so a raw key never reaches a screen
	 */
	text(key, values = {}) {
		const entry = this.entry(key);
		if (!entry) throw new RangeError(`No catalog entry "${key}"`);
		return entry.text.replace(/\{([a-z]+)\}/g, (placeholder, name) => (name in values ? String(values[name]) : placeholder));
	}

	/**
	 * Explanation of a management error or failure code, generic for a code this version does not know
	 *
	 * @param {string} code
	 */
	error(code) {
		return this.text(this.has(`error.${code}`) ? `error.${code}` : 'error.unknown', { code });
	}
}
